import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';

import {
    ProjectRef,
    TaskAction,
    TaskId,
    TaskState,
    TaskStateObject,
} from 'core/model';
import { AppDispatch } from 'store';
import { openModal } from 'store/layoutSlice';
import { deleteTask, renameTask, taskAction } from 'store/tasksThunks';

import TaskName from 'layout/atoms/TaskName';
import TextEditable from 'layout/atoms/TextEditable';

import { ReactComponent as MenuIcon } from 'assets/icons/dots-horizontal.svg';
import { ReactComponent as EditIcon } from 'assets/icons/edit1.svg';
import { ReactComponent as NoteIcon } from 'assets/icons/file.svg';
import { ReactComponent as TrashIcon } from 'assets/icons/trash.svg';

import styles from './task.module.scss';

type Props = {
    task: TaskStateObject;
    projectRef: ProjectRef;
    deletable?: boolean;
    /** Project color, used to tint the note indicator. */
    color?: string | null;
};

const Task = ({ task, projectRef, deletable = true, color }: Props) => {
    const { t } = useTranslation();
    const [taskState, setTaskState] = useState<TaskState>(task.state);
    const [isDeleted, setIsDeleted] = useState<boolean>(false);
    const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [taskName, setTaskName] = useState<string>(task.name);
    const [touchStart, setTouchStart] = useState<number | null>(null);
    const [touchEnd, setTouchEnd] = useState<number | null>(null);
    const [isDragging, setIsDragging] = useState<boolean>(false);
    const menuRef = useRef<HTMLDivElement>(null);
    const taskRef = useRef<HTMLDivElement>(null);

    const dispatch: AppDispatch = useDispatch();

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                menuRef.current &&
                !menuRef.current.contains(event.target as Node)
            ) {
                setIsMenuOpen(false);
            }
        };

        if (isMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMenuOpen]);

    const doWorkspaceTaskAction = async (action: TaskAction) => {
        return dispatch(
            taskAction({
                taskId: task.id as TaskId,
                action,
                command: {
                    origin: 'workspace',
                    workspaceId: projectRef.workspaceId,
                    projectId: projectRef.projectId,
                },
            })
        );
    };

    const taskClicked = (event: any) => {
        if (taskState === 'IDLE') {
            setTaskState('PLANNED');
            doWorkspaceTaskAction('PLAN').catch(() => {
                setTaskState('IDLE');
            });
        } else if (taskState === 'PLANNED') {
            setTaskState('IDLE');
            doWorkspaceTaskAction('IDLE').catch(() => {
                setTaskState('PLANNED');
            });
        }
    };

    const menuClicked = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsMenuOpen(!isMenuOpen);
    };

    const editClicked = () => {
        setIsMenuOpen(false);
        setIsEditing(true);
    };

    const notesClicked = (event?: React.MouseEvent) => {
        // Keep the click off the row, which would toggle the task state.
        event?.stopPropagation();
        setIsMenuOpen(false);
        dispatch(
            openModal({
                name: 'taskNotes',
                props: { taskId: task.id },
            })
        );
    };

    const trashClicked = () => {
        setIsMenuOpen(false);
        setIsDeleted(true);
        doDeleteTask().catch(() => {
            setIsDeleted(false);
        });
    };

    const handleTaskNameChange = (event: any) => {
        setTaskName(event.target.value);
    };

    const handleTaskNameKeyDown = (event: KeyboardEvent) => {
        event.stopPropagation();
        if (event.key === 'Enter') {
            event.preventDefault();
            confirmTaskNameEdit();
        } else if (event.key === 'Escape') {
            cancelTaskNameEdit();
        }
    };

    const confirmTaskNameEdit = () => {
        const trimmedName = taskName.trim();

        if (!trimmedName) {
            setTaskName(task.name);
        } else if (trimmedName !== task.name) {
            dispatch(
                renameTask({
                    taskId: task.id as TaskId,
                    command: {
                        workspaceId: projectRef.workspaceId,
                        projectId: projectRef.projectId,
                        name: trimmedName,
                    },
                })
            ).catch(() => {
                setTaskName(task.name);
            });
        }
        setIsEditing(false);
    };

    const cancelTaskNameEdit = () => {
        setTaskName(task.name);
        setIsEditing(false);
    };

    const handleTouchStart = (e: React.TouchEvent) => {
        if (isEditing) return;
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e: React.TouchEvent) => {
        if (isEditing) return;
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (isEditing || !touchStart || !touchEnd) return;

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && !isMenuOpen) {
            setIsMenuOpen(true);
        } else if (isRightSwipe && isMenuOpen) {
            setIsMenuOpen(false);
        }
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        if (isEditing) return;
        setIsDragging(true);
        setTouchStart(e.clientX);
        setTouchEnd(null);
    };

    const handleMouseMove = (e: React.MouseEvent) => {
        if (isEditing || !isDragging) return;
        setTouchEnd(e.clientX);
    };

    const handleMouseUp = () => {
        if (isEditing || !isDragging || !touchStart || touchEnd === null) {
            setIsDragging(false);
            return;
        }

        const distance = touchStart - touchEnd;
        const isLeftSwipe = distance > 50;
        const isRightSwipe = distance < -50;

        if (isLeftSwipe && !isMenuOpen) {
            setIsMenuOpen(true);
        } else if (isRightSwipe && isMenuOpen) {
            setIsMenuOpen(false);
        }

        setIsDragging(false);
    };

    const doDeleteTask = () => {
        return dispatch(
            deleteTask({
                taskId: task.id as TaskId,
                command: {
                    origin: 'workspace',
                    workspaceId: projectRef.workspaceId,
                    projectId: projectRef.projectId,
                },
            })
        );
    };

    if (isDeleted) {
        return null;
    }

    return (
        <>
            <div
                className={`${styles.wrap} ${styles[taskState]} ${
                    isMenuOpen ? styles.menuActive : ''
                }`}
                ref={taskRef}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
            >
                <button
                    className={`${styles.box} ${styles[taskState]}`}
                    onClick={taskClicked}
                    style={{
                        alignSelf: 'stretch',
                        alignContent: 'center',
                    }}
                >
                    <span />
                </button>
                <div
                    className={`${styles.text} ${styles[taskState]}`}
                    onClick={!isEditing ? taskClicked : undefined}
                >
                    {isEditing ? (
                        <TextEditable
                            value={taskName}
                            handleChange={handleTaskNameChange}
                            handleKeyDown={handleTaskNameKeyDown}
                            handleBlur={confirmTaskNameEdit}
                            maxLength={200}
                            placeholder="Enter task name"
                            onRenameConfirmedButtonClicked={confirmTaskNameEdit}
                            cursorAtEnd={true}
                        />
                    ) : (
                        <TaskName name={taskName}></TaskName>
                    )}
                </div>

                {/* Main content that slides */}
                <div
                    className={`${styles.mainContent} ${
                        isMenuOpen ? styles.contentSlide : ''
                    }`}
                >
                    {task.hasNotes && !isMenuOpen && (
                        <button
                            className={styles.noteIndicator}
                            onClick={notesClicked}
                            title={t('TASK.NOTES.OPEN')}
                            aria-label={t('TASK.NOTES.OPEN')}
                            style={color ? { color } : undefined}
                        >
                            <NoteIcon />
                        </button>
                    )}
                    {deletable && !isMenuOpen && (
                        <button
                            className={`${styles['button']}`}
                            onClick={menuClicked}
                        >
                            <MenuIcon />
                        </button>
                    )}
                </div>

                {/* Slide menu from right */}
                {deletable && (
                    <div
                        className={`${styles.slideMenu} ${
                            isMenuOpen ? styles.menuOpen : ''
                        }`}
                        ref={menuRef}
                    >
                        <button
                            className={styles.menuAction}
                            onClick={editClicked}
                        >
                            <EditIcon />
                            <span>{t('PROJECT.RENAME')}</span>
                        </button>
                        <button
                            className={styles.menuAction}
                            onClick={notesClicked}
                        >
                            <NoteIcon />
                            <span>
                                {task.hasNotes
                                    ? t('TASK.NOTES.TITLE')
                                    : t('TASK.NOTES.ADD_ACTION')}
                            </span>
                        </button>
                        <button
                            className={styles.menuAction}
                            onClick={trashClicked}
                        >
                            <TrashIcon />
                            <span>{t('PROJECT.DELETE')}</span>
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default Task;
